'use client';

/**
 * WebQAConversationCard — Reception "conversation" lens.
 *
 * What went over the wire each step. Header reads useDocsStore (CV_ id,
 * the in-flight OODA-phase preview while llmLoading); once the turn is
 * recorded, fires `docs.conversation` and renders the raw :Message stream
 * in chronological order — every wire interaction (SUBMIT, OBSERVE
 * sub-steps, ORIENT/answer, ACT/followups), each row showing the OODA
 * phase, the cycle, the author, the MSG_ id, the body (the wire content),
 * and the cycle's step_context (raw metadata) tucked underneath. Audit
 * raw — no display-string contrivance, no Q/A grouping.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDocsStore } from '@/lib/stores/docs-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import { api } from '@/lib/api/client';
import type { DocsConversation, DocsConversationMessage } from '@/lib/api/types';
import { useLanguageStore } from '@/lib/stores/language-store';
import { QA_SECTION, QA_BOX } from './web-qa-chrome';


const fmt = (n: number | undefined | null): string =>
  typeof n === 'number' ? n.toLocaleString() : '—';

// OODA phases in canonical order; phase_timings keys arrive as `<name>_ms`.
const PHASE_ORDER = ['condense', 'observe', 'decide', 'synthesize', 'learn'];

function orderedPhases(timings: Record<string, number>): Array<[string, number]> {
  const norm: Record<string, number> = {};
  for (const [k, v] of Object.entries(timings)) norm[k.replace(/_ms$/, '')] = Number(v);
  const known: Array<[string, number]> = PHASE_ORDER
    .filter((p) => p in norm)
    .map((p) => [p, norm[p]]);
  const extra: Array<[string, number]> = Object.entries(norm).filter(
    ([p]) => !PHASE_ORDER.includes(p),
  );
  return [...known, ...extra];
}


function MessageRow({ m, showTurn }: { m: DocsConversationMessage; showTurn: boolean }) {
  const T = useCardTypography();
  const lang = useLanguageStore((s) => s.lang) ?? undefined;

  const [bodyOpen, setBodyOpen] = useState(false);
  const [ctxOpen, setCtxOpen] = useState(false);
  const hasCtx = !!(m.step_context && m.step_context !== 'null');

  return (
    <div className="border-l-2 border-border/50 pl-3 py-2">
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        <span className={cn(T.metadata, 'font-medium text-foreground')}>{m.step || '—'}</span>
        {m.strategy && (
          <span className={cn(T.metadata, 'text-muted-foreground')}>· {m.strategy}</span>
        )}
        {m.author && (
          <span className={cn(T.metadata, 'text-muted-foreground/70')}>· {m.author}</span>
        )}
        {showTurn && (
          <span className={cn(T.metadata, 'text-muted-foreground/70')}>· turn {m.turn}</span>
        )}
        <span className={cn(T.metadata, 'font-mono text-muted-foreground/60 ml-auto')}>
          {m.message_sid}
        </span>
      </div>

      {/* body — what went over the wire for this step */}
      {m.body && (
        <pre
          onClick={() => setBodyOpen((v) => !v)}
          className={cn(
            T.metadata,
            'text-[11px] text-foreground/90 mt-1 whitespace-pre-wrap break-words font-sans cursor-pointer',
            bodyOpen ? 'max-h-96 overflow-auto' : 'line-clamp-3',
          )}
        >
          {m.body}
        </pre>
      )}

      {/* step_context — cycle metadata (collapsed by default, raw JSON) */}
      {hasCtx && (
        <>
          <button
            type="button"
            onClick={() => setCtxOpen((v) => !v)}
            className={cn(
              T.metadata,
              'text-[10px] uppercase tracking-wider text-muted-foreground/60 mt-1 hover:text-muted-foreground',
            )}
          >
            {ctxOpen ? '▾' : '▸'} step_context
          </button>
          {ctxOpen && (
            <pre
              className={cn(
                T.metadata,
                'text-[10px] font-mono text-muted-foreground mt-1 whitespace-pre-wrap break-words max-h-64 overflow-auto',
              )}
            >
              {m.step_context}
            </pre>
          )}
        </>
      )}
    </div>
  );
}


export function WebQAConversationCard() {
  const T = useCardTypography();
  const response = useDocsStore((s) => s.response);
  const llmLoading = useDocsStore((s) => s.llmLoading);
  const convStrategy =
    useCapabilityStore((s) => s.strategyName('docs', 'conversation')) ?? 'docs_conversation';

  const convId = response?.conversation_id ?? null;
  const turn = response?.turn ?? 0;

  const { data: conv } = useQuery<DocsConversation>({
    queryKey: ['docs', 'conversation', convId, turn],
    queryFn: async () => {
      if (!convStrategy || !convId) {
        throw new Error('docs.conversation strategy or conversation_id missing');
      }
      const res = await api.getStrategyData<DocsConversation>(convStrategy, {
        conversation_id: convId,
      });
      if (!res.success) throw new Error(res.error || `${convStrategy} failed`);
      return res.data;
    },
    enabled: Boolean(convId && convStrategy),
    staleTime: 30_000,
  });

  const messages: DocsConversationMessage[] = conv?.messages ?? [];

  if (!response) return null;

  const u = response.usage;
  const previewPhases = orderedPhases(response.phase_timings ?? {});
  const recorded = response.llm_status === 'completed';
  const haveMessages = messages.length > 0;

  const turnCount = conv?.turn_count || turn;
  const msgCount = conv?.message_count || messages.length;
  const totalTok = conv?.total_tokens ?? u?.total_tokens ?? null;
  const showTurn = turnCount > 1;

  return (
    <section className={QA_SECTION}>
      <div className="w-full">
        <div className={QA_BOX}>
          {/* heading + the CV_ id */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className={cn(T.metadata, 'uppercase tracking-wider text-xs')}>Conversation</div>
            <span className={cn(T.metadata, 'font-mono text-xs text-muted-foreground break-all')}>
              {response.conversation_id}
            </span>
          </div>

          {/* rollup */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border bg-muted text-muted-foreground border-border">
              {turnCount} turn{turnCount === 1 ? '' : 's'}
            </span>
            <span className={cn(T.metadata, 'text-muted-foreground')}>
              {msgCount} :Message node{msgCount === 1 ? '' : 's'}
            </span>
            <span className={cn(T.metadata, 'text-muted-foreground')}>· {fmt(totalTok)} tok</span>
            {conv?.llm_model && (
              <span className={cn(T.metadata, 'text-muted-foreground')}>· {conv.llm_model}</span>
            )}
          </div>

          {/* the wire trail — every :Message, in order */}
          {haveMessages ? (
            <div className="flex flex-col gap-1 max-h-[36rem] overflow-auto pr-1">
              {messages.map((m) => (
                <MessageRow key={m.message_sid || `${m.turn}-${m.step}`} m={m} showTurn={showTurn} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span className={cn(T.metadata, 'text-muted-foreground')}>
                {llmLoading
                  ? 'recording — synthesis running…'
                  : recorded
                    ? 'turn recorded — loading thread…'
                    : 'preview turn — not yet recorded (LLM available on request)'}
              </span>
              {previewPhases.length > 0 && (
                <div className="flex flex-col gap-1">
                  {previewPhases.map(([name, ms]) => (
                    <div
                      key={name}
                      className={cn(T.metadata, 'flex items-center justify-between text-xs')}
                    >
                      <span className="text-foreground">{name}</span>
                      <span className="text-muted-foreground tabular-nums">{Math.round(ms)} ms</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
